/**
 * FB2 Converter - Main API
 * Handles FB2 and FB2.zip files, converts to MD.zip bundles
 */

import JSZip from 'jszip';
import { FB2Parser } from './fb2-parser';
import { FB2ToMarkdownConverter } from './fb2-to-markdown';

export interface ConvertedBundle {
  readme: string;
  assets: Map<string, { contentType: string; data: ArrayBuffer }>;
  title: string;
  metadata: {
    author?: string;
    genre?: string[];
    lang?: string;
    date?: string;
  };
}

export interface ConversionProgress {
  stage: 'extracting' | 'parsing' | 'converting' | 'processing-images' | 'creating-bundle' | 'complete';
  progress: number; // 0-100
  message: string;
}

export class FB2Converter {
  private parser = new FB2Parser();
  private converter = new FB2ToMarkdownConverter();

  /**
   * Convert FB2 or FB2.zip file to MD.zip bundle
   */
  async convert(
    file: File | ArrayBuffer,
    onProgress?: (progress: ConversionProgress) => void
  ): Promise<ConvertedBundle> {
    let fb2Content: string;
    let isZipped = false;

    // Stage 1: Extracting
    onProgress?.({
      stage: 'extracting',
      progress: 10,
      message: 'Extracting FB2 file...'
    });

    if (file instanceof File) {
      isZipped = file.name.toLowerCase().endsWith('.zip');
      const arrayBuffer = await file.arrayBuffer();
      fb2Content = await this.extractFB2Content(arrayBuffer, isZipped);
    } else {
      // ArrayBuffer - assume it's zipped if we can parse it as ZIP
      try {
        const zip = await JSZip.loadAsync(file);
        fb2Content = await this.extractFB2FromZip(zip);
        isZipped = true;
      } catch {
        // Not a ZIP, treat as plain FB2 XML
        fb2Content = new TextDecoder('utf-8').decode(file);
        isZipped = false;
      }
    }

    // Stage 2: Parsing
    onProgress?.({
      stage: 'parsing',
      progress: 30,
      message: 'Parsing FB2 XML structure...'
    });
    const fb2Book = this.parser.parseXML(fb2Content);

    // Stage 3: Converting
    onProgress?.({
      stage: 'converting',
      progress: 50,
      message: 'Converting to Markdown format...'
    });
    const result = this.converter.convert(fb2Book);

    // Stage 4: Processing images
    onProgress?.({
      stage: 'processing-images',
      progress: 70,
      message: `Processing ${fb2Book.images.size} images...`
    });

    // Prepare assets
    const assets = new Map<string, { contentType: string; data: ArrayBuffer }>();

    for (const [id, image] of fb2Book.images) {
      // Decode base64 to ArrayBuffer
      const binaryString = atob(image.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Determine file extension from content type
      const ext = this.getExtensionFromContentType(image.contentType);
      const filename = id.includes('.') ? id : `${id}.${ext}`;

      assets.set(`assets/${filename}`, {
        contentType: image.contentType,
        data: bytes.buffer
      });
    }

    onProgress?.({
      stage: 'complete',
      progress: 90,
      message: 'Conversion complete!'
    });

    return {
      readme: result.markdown,
      assets,
      title: fb2Book.metadata.title,
      metadata: {
        ...(fb2Book.metadata.author !== undefined && { author: fb2Book.metadata.author }),
        ...(fb2Book.metadata.genre !== undefined && { genre: fb2Book.metadata.genre }),
        ...(fb2Book.metadata.lang !== undefined && { lang: fb2Book.metadata.lang }),
        ...(fb2Book.metadata.date !== undefined && { date: fb2Book.metadata.date })
      }
    };
  }

  /**
   * Create MD.zip bundle from converted FB2
   */
  async createMDZipBundle(
    converted: ConvertedBundle,
    onProgress?: (progress: ConversionProgress) => void
  ): Promise<ArrayBuffer> {
    onProgress?.({
      stage: 'creating-bundle',
      progress: 95,
      message: 'Creating MD.zip bundle...'
    });

    const zip = new JSZip();

    // Add README.md
    zip.file('README.md', converted.readme);

    // Add assets
    for (const [path, asset] of converted.assets) {
      zip.file(path, asset.data);
    }

    // Generate ZIP with DEFLATE compression
    const result = await zip.generateAsync({
      type: 'arraybuffer',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 9  // Maximum compression
      }
    });

    onProgress?.({
      stage: 'complete',
      progress: 100,
      message: 'Bundle created successfully!'
    });

    return result;
  }

  private async extractFB2Content(arrayBuffer: ArrayBuffer, isZipped: boolean): Promise<string> {
    if (!isZipped) {
      return new TextDecoder('utf-8').decode(arrayBuffer);
    }

    const zip = await JSZip.loadAsync(arrayBuffer);
    return await this.extractFB2FromZip(zip);
  }

  private async extractFB2FromZip(zip: JSZip): Promise<string> {
    // Find FB2 file in ZIP
    const fb2Files = Object.keys(zip.files).filter(name =>
      name.toLowerCase().endsWith('.fb2')
    );

    if (fb2Files.length === 0) {
      throw new Error('No FB2 file found in ZIP archive');
    }

    if (fb2Files.length > 1) {
      throw new Error('Multiple FB2 files found in ZIP archive');
    }

    const fb2FileName = fb2Files[0];
    if (!fb2FileName) {
      throw new Error('FB2 file name is undefined');
    }
    const fb2File = zip.files[fb2FileName];
    if (!fb2File) {
      throw new Error('FB2 file not found in ZIP');
    }
    return await fb2File.async('text');
  }

  private getExtensionFromContentType(contentType: string): string {
    const map: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/bmp': 'bmp',
      'image/webp': 'webp',
      'image/svg+xml': 'svg'
    };

    return map[contentType.toLowerCase()] || 'jpg';
  }
}
